// externals
import type { BaseDataLayerV3, EventMapV3 } from '@makemydeal/dr-common-data-layer-utils';
import { buildCDLEventMiddlewareV3, buildReadyEventV3, buildUserEventV3 } from '@makemydeal/dr-common-data-layer-utils';

// libraries
// consts/enums
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { MiddlewareAPI } from 'redux';

import * as baseDealActionTypes from '../actionTypes/baseDealActionTypes';
import * as baseDealAnalyticsActionTypes from '../actionTypes/baseDealAnalyticsActionTypes';
import * as customerSpdTypes from '../actionTypes/CustomerSpdTypes';
import * as dealLockingActionTypes from '../actionTypes/dealLockingActionTypes';
import * as deskingActionTypes from '../actionTypes/deskingActionTypes';
import * as insuranceActionTypes from '../actionTypes/insuranceActionTypes';
import * as tradeActionTypes from '../actionTypes/tradeActionTypes';
import * as dueAtSigningActionTypes from '../actionTypes/dueAtSigningActionTypes';
import * as interactionAnalyticsActionTypes from '../actionTypes/interactionAnalyticsActionTypes';
import * as pushToRouteOneActionTypes from '../actionTypes/pushToRouteOneActionTypes';
import * as fuelEconomyActionTypes from '../actionTypes/fuelEconomyActionTypes';
import * as fuelSavingActionTypes from '@makemydeal/dr-activities-fuel-saving';
import * as tradeReconCostActionTypes from '../actionTypes/tradeReconCostActionTypes';
import * as proposalActionTypes from '../actionTypes/proposalActionTypes';
import * as vehicleActionTypes from '../actionTypes/vehicleActionTypes';
import * as recentDealsAnalyticsActionTypes from '../actionTypes/recentDealsAnalyticsActionTypes';
import * as multiScenarioActionTypes from '../actionTypes/multiScenarioActionTypes';
import { actions as offerReduxActions, selectors as offerReduxSelectors } from '@makemydeal/dr-offer-redux';

import { RequestType } from '@makemydeal/dr-platform-types';
import { CDL_V3_EVENT } from '@makemydeal/dr-shared-types';
import type { CdlDealInfo, CdlEventInfo, CdlAltDealStructurePrediction } from '../types/cdl';
import type { VehicleAnalyticsPayload } from '../types/analyticsPayloadTypes';
import { CDL_USER_INTENT } from '../constants';
import { getCurrentSlideOut } from '../selectors/BaseDealSelectors';
import { openDealCdlEvents } from './openDealCdlEvents';

// Split AI alt deal structure items by deal type: finance (AI-generated) → deal.predictions.dealStructure,
// lease (non-AI) → deal.dealStructure. A bucket is included only when it has items.
const buildAltDealStructureDeal = (structures?: CdlAltDealStructurePrediction[]): CdlDealInfo => {
    if (!structures?.length) return {};
    const finance = structures.filter((structure) => structure.dealType !== RequestType.LEASE);
    const lease = structures.filter((structure) => structure.dealType === RequestType.LEASE);
    const deal: CdlDealInfo = {};
    if (finance.length) deal.predictions = { dealStructure: finance };
    if (lease.length) deal.dealStructure = lease;
    return deal;
};

const toCdlVehicle = (vehicle?: VehicleAnalyticsPayload) => ({
    vin: vehicle?.vin,
    make: vehicle?.make,
    model: vehicle?.model,
    year: vehicle?.year
});

// DealPulse predictions (dc-mv-306) are reported as 'n/a' rather than omitted when Deal Pulse has no value.
const toDealPulseValue = (value: number | string | null | undefined): number | string => value ?? 'n/a';

let isCdlDataReady: boolean = false;
const eventsBeforeDataReady: Array<AnyFSA> = [{ type: baseDealAnalyticsActionTypes.VIEW_MANAGER_PAGE }];

// Merge caller-supplied fields into an event's `deal` object at emit time. Callers pass a plain
// `Partial<CdlDealInfo>`; each wrapper builds that object inline with whatever state-derived fields
// it needs. To add a new per-event deal field later, add another key to the object literal in the
// wrapper — no factory or type gymnastics required.
export const withDynamicFields = (eventData: CdlEventInfo, dealExtras: Partial<CdlDealInfo>): CdlEventInfo => ({
    ...eventData,
    deal: { ...eventData.deal, ...dealExtras }
});

export const buildEventWrapper = (eventData: CdlEventInfo) => (action: AnyFSA, currentState?: any) => {
    if (!isCdlDataReady) {
        eventsBeforeDataReady.push(action);
        return { event: {} };
    } else {
        return buildUserEventV3(
            withDynamicFields(eventData, {
                autoSelectProgramCheckboxValue: currentState ? !offerReduxSelectors.getIsUserProgramQuotes(currentState) : false
            })
        );
    }
};

// Dynamic event builder with CDL readiness check.
// Optional `predicate` lets a handler ride on an existing broadly-dispatched action type
// and only emit when the dispatch site opted in — e.g. via a `meta.userIntent` marker.
// Returns `undefined` when the predicate rejects — the CDL middleware short-circuits on
// falsy returns (see `buildCDLEventMiddlewareV3`), so no empty event lands on Pixall.
const buildDynamicEvent =
    (
        eventDataBuilder: (action: AnyFSA, currentState?: any) => CdlEventInfo,
        predicate?: (action: AnyFSA, currentState?: any) => boolean
    ) =>
    (action: AnyFSA, currentState?: any) => {
        if (predicate && !predicate(action, currentState)) {
            return undefined as any;
        }
        if (!isCdlDataReady) {
            eventsBeforeDataReady.push(action);
            return { event: {} };
        }
        const eventData = eventDataBuilder(action, currentState);
        return buildUserEventV3(
            withDynamicFields(eventData, {
                autoSelectProgramCheckboxValue: currentState ? !offerReduxSelectors.getIsUserProgramQuotes(currentState) : false
            })
        );
    };

// dc-mv-312: shared between UPDATED_PAYMENT_GRID_TOGGLE and UPDATED_FLEX_BUY_PAYMENT_GRID_TOGGLE.
// Downstream Pixall segmentation treats both toggles as one interaction.
const paymentGridToggleHandler = buildDynamicEvent(
    (action: AnyFSA) => ({
        action: 'Click',
        element: 'Payment Grid Toggle',
        location: ['Deal Dashboard', 'Deal Details', 'Payment Grid'],
        intention: 'Toggle Payment Grid',
        tagIndex: 'dc-mv-312',
        toggleState: action.payload,
        application: { name: 'Manager View' }
    }),
    (action: AnyFSA) => action.meta?.userIntent === CDL_USER_INTENT.PAYMENT_GRID_TOGGLED
);

// Generic CDL v3 Event Handler - forwards library-created events
const forwardCDLEvent = (action: any) => {
    /* istanbul ignore if */
    if (!isCdlDataReady) {
        eventsBeforeDataReady.push(action);
        return { event: {} };
    } else {
        // Library already created the complete event data - just forward it
        return buildUserEventV3(action.payload);
    }
};

export const userEvents: EventMapV3 = {
    [deskingActionTypes.CLICKED_COPY_DEAL_URL_CLICKED]: buildEventWrapper({
        action: 'Click',
        element: 'Get Deal Link - Copy Button',
        location: ['Deal Dashboard', 'Current Working Offer', 'Modal'],
        intention: 'Copy Link To Deal'
    }),
    // Generic CDL v3 Event Forwarder - handles events from any library
    [CDL_V3_EVENT]: forwardCDLEvent,
    [proposalActionTypes.PAYMENT_GRID_DOWN_PAYMENT_CHECKBOX_CLICKED]: buildEventWrapper({
        action: 'Click',
        element: 'Payment Grid Down Payment Checkbox Print Proposal',
        location: ['Deal Dashboard'],
        intention: 'Edit Payment Grid Down Payment in Print Proposal',
        tagIndex: 'dc-mv-323',
        application: { name: 'Manager View' }
    }),
    [proposalActionTypes.PAYMENT_GRID_TERM_CHECKBOX_CLICKED]: buildEventWrapper({
        action: 'Click',
        element: 'Payment Grid Term Checkbox Print Proposal',
        location: ['Deal Dashboard'],
        intention: 'Edit Payment Grid Term in Print Proposal',
        tagIndex: 'dc-mv-324',
        application: { name: 'Manager View' }
    }),
    [recentDealsAnalyticsActionTypes.CREATE_QUOTE_DEAL_LIST_CLICKED]: buildEventWrapper({
        action: 'Click',
        element: 'Create Quote Deal List Button',
        location: ['Deal Dashboard'],
        intention: 'Create a quote within the deal list',
        tagIndex: 'dc-mv-325',
        application: { name: 'Manager View' }
    }),
    [vehicleActionTypes.INVENTORY_TYPE_SELECT_CLICKED]: buildEventWrapper({
        action: 'Click',
        element: 'Select Inventory Type Deal List Button',
        location: ['Deal Dashboard'],
        intention: 'Select inventory type before proceeding with the deal',
        tagIndex: 'dc-mv-326',
        application: { name: 'Manager View' }
    }),
    [multiScenarioActionTypes.COPY_DEAL_SCENARIO_CLICKED]: buildEventWrapper({
        action: 'Click',
        element: 'Copy Deal Scenario Button',
        location: ['Deal Dashboard'],
        intention: 'Copy Deal Scenario',
        tagIndex: 'dc-mv-150',
        application: { name: 'Manager View' }
    }),
    [multiScenarioActionTypes.DELETE_DEAL_SCENARIO_CLICKED]: buildEventWrapper({
        action: 'Click',
        element: 'Delete Deal Scenario Button',
        location: ['Deal Dashboard'],
        intention: 'Delete Deal Scenario',
        tagIndex: 'dc-mv-151',
        application: { name: 'Manager View' }
    }),
    [multiScenarioActionTypes.SELECT_PRIMARY_DEAL_SCENARIO_CLICKED]: buildEventWrapper({
        action: 'Click',
        element: 'Select Deal Scenario Button',
        location: ['Deal Dashboard'],
        intention: 'Select Primary Deal Scenario',
        tagIndex: 'dc-mv-152',
        application: { name: 'Manager View' }
    }),
    [dealLockingActionTypes.LOCK_PAYMENT_INPUTS_CLICKED]: buildDynamicEvent((action: AnyFSA) => {
        // Payload is { isLocking: true/false }
        const isLocking = action.payload?.isLocking;

        return {
            action: 'Click',
            element: isLocking ? 'Activate Deal Lock Icon' : 'Deactivate Deal Lock Icon',
            intention: isLocking ? 'Activate deal payment values lock' : 'Deactivate deal payment values lock',
            location: ['Deal Dashboard'],
            tagIndex: isLocking ? 'dc-mv-203' : 'dc-mv-202'
        };
    }),
    [deskingActionTypes.CLICKED_PUSH_TO_DMS_CONFIRM]: buildEventWrapper({
        action: 'Click',
        element: 'Push to DMS - Confirm Button',
        location: ['Deal Dashboard', 'Deal Progress', 'Modal'],
        intention: 'Navigate to DMS'
    }),
    [deskingActionTypes.CLICKED_BEGIN_CHECKOUT]: buildEventWrapper({
        action: 'Click',
        element: 'Begin Checkout Button',
        location: ['Deal Dashboard', 'Footer'],
        intention: 'Generate Checkout Link'
    }),
    [deskingActionTypes.SEND_TO_SALES_VIEW_CONFIRM]: buildEventWrapper({
        action: 'Click',
        element: 'Send To Sales View - Confirm Button',
        location: ['Deal Dashboard', 'Current Working Offer', 'Modal'],
        intention: 'Send Offer To Sales View'
    }),
    [deskingActionTypes.CLICKED_SEND_TO_CUSTOMER]: buildEventWrapper({
        action: 'Click',
        element: 'Send To Customer - Button',
        location: ['Deal Dashboard', 'Current Working Offer'],
        intention: 'Send Offer To Customer'
    }),
    [deskingActionTypes.CLICKED_CONTINUE_IN_DESKING_CONFIRM]: buildEventWrapper({
        action: 'Click',
        element: 'Continue In Desking - Confirm Button',
        location: ['Deal Dashboard', 'Current Working Offer', 'Modal'],
        intention: 'Navigate To Desking'
    }),
    [insuranceActionTypes.CLICK_INSURANCE_SLIDEOUT_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Insurance Slideout Button',
        location: ['Deal Dashboard', 'Header'],
        intention: 'Open Insurance Slideout'
    }),
    [insuranceActionTypes.VIEW_INSURANCE_SLIDEOUT]: buildEventWrapper({
        action: 'View',
        element: 'Insurance Slideout',
        location: ['Deal Dashboard', 'Insurance Slideout'],
        intention: 'Open Insurance Slideout'
    }),
    [insuranceActionTypes.CLOSE_INSURANCE_SLIDEOUT]: buildEventWrapper({
        action: 'Click',
        element: 'Insurance Slideout',
        location: ['Deal Dashboard', 'Insurance Slideout'],
        intention: 'Close Insurance Slideout'
    }),
    [insuranceActionTypes.SELECT_INSURANCE_PROVIDER_NAME]: buildEventWrapper({
        action: 'Click',
        element: 'Insurance Provider Name Field',
        location: ['Deal Dashboard', 'Insurance Slideout'],
        intention: 'Select Insurance Provider Name'
    }),
    [insuranceActionTypes.SEARCH_INSURANCE_PROVIDER_NAME]: buildEventWrapper({
        action: 'Click',
        element: 'Insurance Provider Name Field',
        location: ['Deal Dashboard', 'Insurance Slideout'],
        intention: 'Search Insurance Provider Name'
    }),
    [insuranceActionTypes.AUTOCOMPLETE_INSURANCE_PROVIDER_NAME]: buildEventWrapper({
        action: 'Click',
        element: 'Insurance Provider Name Field',
        location: ['Deal Dashboard', 'Insurance Slideout'],
        intention: 'Autocomplete Insurance Provider Name'
    }),
    [insuranceActionTypes.SAVE_INSURANCE_PROVIDER]: buildEventWrapper({
        action: 'Click',
        element: 'Insurance Slideout',
        location: ['Deal Dashboard', 'Insurance Slideout'],
        intention: 'Save Insurance Provider'
    }),
    [insuranceActionTypes.CANCEL_INSURANCE_PROVIDER]: buildEventWrapper({
        action: 'Click',
        element: 'Insurance Slideout',
        location: ['Deal Dashboard', 'Insurance Slideout'],
        intention: 'Cancel Insurance Provider'
    }),
    [tradeActionTypes.SELECT_TRADE_IN_LIENHOLDER_NAME]: buildEventWrapper({
        action: 'Click',
        element: 'Trade-in Lienholder Name Field',
        location: ['Deal Dashboard', 'Deal Details', 'Trade-in'],
        intention: 'Select Trade-in Lienholder Name'
    }),
    [tradeActionTypes.SEARCH_TRADE_IN_LIENHOLDER_NAME]: buildEventWrapper({
        action: 'Click',
        element: 'Trade-in Lienholder Name Field',
        location: ['Deal Dashboard', 'Deal Details', 'Trade-in'],
        intention: 'Search Trade-in Lienholder Name'
    }),
    [tradeActionTypes.AUTOCOMPLETE_TRADE_IN_LIENHOLDER_NAME]: buildEventWrapper({
        action: 'Click',
        element: 'Trade-in Lienholder Name Field',
        location: ['Deal Dashboard', 'Deal Details', 'Trade-in'],
        intention: 'Autocomplete Trade-in Lienholder Name'
    }),
    [baseDealAnalyticsActionTypes.VIEWED_SUMMARY_TAB]: buildEventWrapper({
        action: 'View',
        element: 'Deal Summary Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'View Deal Summary Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_SUMMARY_TAB]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Summary Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Summary Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_SUMMARY_TAB_HOTKEY]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Summary Tab Hotkey',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Summary Tab'
    }),
    [baseDealAnalyticsActionTypes.VIEWED_TRADEIN_TAB]: buildEventWrapper({
        action: 'View',
        element: 'Deal Trade-in Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'View Deal Trade-in Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_TRADEIN_TAB]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Trade-in Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Trade-in Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_TRADEIN_TAB_HOTKEY]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Trade-in Tab Hotkey',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Trade-in Tab'
    }),
    [baseDealAnalyticsActionTypes.VIEWED_FEES_TAB]: buildEventWrapper({
        action: 'View',
        element: 'Deal Fees Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'View Deal Fees Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_FEES_TAB]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Fees Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Fees Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_FEES_TAB_HOTKEY]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Fees Tab Hotkey',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Fees Tab'
    }),
    [baseDealAnalyticsActionTypes.VIEWED_TAXES_TAB]: buildEventWrapper({
        action: 'View',
        element: 'Deal Taxes Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'View Deal Taxes Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_TAXES_TAB]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Taxes Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Taxes Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_TAXES_TAB_HOTKEY]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Taxes Tab Hotkey',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Taxes Tab'
    }),
    [baseDealAnalyticsActionTypes.VIEWED_PROTECTION_TAB]: buildEventWrapper({
        action: 'View',
        element: 'Deal Protection Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'View Deal Protection Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_PROTECTION_TAB]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Protection Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Protection Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_PROTECTION_TAB_HOTKEY]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Protection Tab Hotkey',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Protection Tab'
    }),
    [baseDealAnalyticsActionTypes.VIEWED_INCENTIVES_TAB]: buildEventWrapper({
        action: 'View',
        element: 'Deal Incentives Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'View Deal Incentives Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_INCENTIVES_TAB]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Incentives Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Incentives Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_INCENTIVES_TAB_HOTKEY]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Incentives Tab Hotkey',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Incentives Tab'
    }),
    [baseDealAnalyticsActionTypes.VIEWED_LENDER_TAB]: buildEventWrapper({
        action: 'View',
        element: 'Deal Lender Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'View Deal Lender Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_LENDER_TAB]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Lender Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Lender Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_LENDER_TAB_HOTKEY]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Lender Tab Hotkey',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Lender Tab'
    }),
    [baseDealAnalyticsActionTypes.VIEWED_ACCESSORIES_TAB]: buildEventWrapper({
        action: 'View',
        element: 'Deal Accessories Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'View Deal Accessories Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_ACCESSORIES_TAB]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Accessories Tab',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Accessories Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_ACCESSORIES_TAB_HOTKEY]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Accessories Tab Hotkey',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Deal Accessories Tab'
    }),
    [baseDealAnalyticsActionTypes.VIEWED_BREAKDOWN_EXPAND]: buildEventWrapper({
        action: 'View',
        element: 'Deal Breakdown Expand',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Open Deal Breakdown'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_BREAKDOWN_EXPAND]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Breakdown Expand',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Open Deal Breakdown'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_BREAKDOWN_EXPAND_HOTKEY]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Breakdown Expand Hotkey',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Open Deal Breakdown'
    }),
    [baseDealAnalyticsActionTypes.VIEWED_BREAKDOWN_COLLAPSE]: buildEventWrapper({
        action: 'View',
        element: 'Deal Breakdown Collapse',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Close Deal Breakdown'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_BREAKDOWN_COLLAPSE]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Breakdown Collapse',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Close Deal Breakdown'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_BREAKDOWN_COLLAPSE_HOTKEY]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Breakdown Collapse Hotkey',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Close Deal Breakdown'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_UPDATE]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Update',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Deal'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_UPDATE_HOTKEY]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Update Hotkey',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Deal'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_NEXT_EVENT_HOTKEY]: buildEventWrapper({
        action: 'Click',
        element: 'Next Event Hotkey',
        location: ['Global'],
        intention: 'Navigate to Next Event'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_PREVIOUS_EVENT_HOTKEY]: buildEventWrapper({
        action: 'Click',
        element: 'Previous Event Hotkey',
        location: ['Global'],
        intention: 'Navigate to Previous Event'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_NEXT_TAB_HOTKEY]: buildEventWrapper({
        action: 'Click',
        element: 'Next Tab Hotkey',
        location: ['Global'],
        intention: 'Navigate to Next Tab'
    }),
    [baseDealAnalyticsActionTypes.CLICKED_PREVIOUS_TAB_HOTKEY]: buildEventWrapper({
        action: 'Click',
        element: 'Previous Tab Hotkey',
        location: ['Global'],
        intention: 'Navigate to Previous Tab'
    }),
    [baseDealAnalyticsActionTypes.VIEW_MANAGER_PAGE]: buildEventWrapper({
        action: 'View',
        element: 'Page',
        location: ['Manager View'],
        intention: 'View Manager View Page',
        tagIndex: 'dc-mv-2'
    }),
    [customerSpdTypes.CLICKED_SHOW_CUSTOMER_SPD]: buildEventWrapper({
        action: 'Click',
        element: 'Show Sensitive Personal Data Button',
        location: ['Deal Dashboard', 'Customer Card'],
        intention: 'Show Customer Sensitive Personal Data'
    }),
    [customerSpdTypes.APPLY_CUSTOMER_CARD_SPD_CHANGES]: buildEventWrapper({
        action: 'Click',
        element: 'Apply Customer Card Changes Button',
        location: ['Deal Dashboard', 'Customer Card'],
        intention: 'Apply Customer Card SPD Changes'
    }),
    [customerSpdTypes.APPLY_CUSTOMER_CARD_NON_SPD_CHANGES]: buildEventWrapper({
        action: 'Click',
        element: 'Apply Customer Card Changes Button',
        location: ['Deal Dashboard', 'Customer Card'],
        intention: 'Apply Customer Card non-SPD Changes'
    }),
    [interactionAnalyticsActionTypes.CLICK_UPLOAD_DOCS_SEND_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Upload Docs Send Link',
        location: ['Deal Dashboard', 'Deal Progress'],
        intention: 'Open Customer Communication Section'
    }),
    [interactionAnalyticsActionTypes.CLICK_CREDIT_APP_SEND_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Credit App Send Link',
        location: ['Deal Dashboard', 'Deal Progress'],
        intention: 'Open Credit App'
    }),
    [interactionAnalyticsActionTypes.CLICK_TEST_DRIVE_SEND_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Test Drive Send Link',
        location: ['Deal Dashboard', 'Deal Progress'],
        intention: 'Open Test Drive App'
    }),
    [interactionAnalyticsActionTypes.CLICK_CUSTOMER_NAME_OR_CO_BUYER_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Open Customer Card for Buyer or Co-Buyer Link',
        location: ['Deal Dashboard', 'Header'],
        intention: 'Open Customer Card'
    }),
    [interactionAnalyticsActionTypes.CLICK_OPEN_DEAL_IN_FI_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Open in F&I Button',
        location: ['Deal Dashboard', 'Deal Progress'],
        intention: 'View F&I Information'
    }),
    [interactionAnalyticsActionTypes.CLICK_CREDIT_BUREAU_SCORES_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Credit Bureau Scores Link',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'View Credit Bureau Information'
    }),
    [interactionAnalyticsActionTypes.SELECT_OFFER_CREDIT_RATING_DROPDOWN]: buildEventWrapper({
        action: 'Select',
        element: 'Offer Credit Rating Dropdown',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Select Customer Credit Score'
    }),
    [interactionAnalyticsActionTypes.VIEW_IN_VAUTO_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'View in vAuto Link',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Open vAuto'
    }),
    [interactionAnalyticsActionTypes.CLICK_MAKE_CURRENT_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Make Current Button',
        location: ['Deal Dashboard', 'Deal Details', 'Modal'],
        intention: 'Switch Deal Scenario to Current'
    }),
    [interactionAnalyticsActionTypes.CLICK_MAKE_CURRENT_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Make Current Link',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Switch Deal Scenario to Current'
    }),
    [interactionAnalyticsActionTypes.CLICK_VALUE_TRADE_IN_START_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Value Trade-In Start Link',
        location: ['Deal Dashboard', 'Deal Progress'],
        intention: 'Open Deal Trade In'
    }),
    [interactionAnalyticsActionTypes.CLICK_TRADE_IN_EDIT_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Trade-In Edit Button',
        location: ['Deal Dashboard'],
        intention: 'Manage trade-in details',
        tagIndex: 'dc-mv-310'
    }),
    [interactionAnalyticsActionTypes.CLICK_VEHICLE_DETAILS_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Vehicle Details Link',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'View Vehicle Details'
    }),
    [interactionAnalyticsActionTypes.CLICK_QUICK_COMPARE_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Quick Compare Link',
        location: ['Deal Dashboard'],
        intention: 'Compare Deal Scenarios'
    }),
    [interactionAnalyticsActionTypes.CLICK_OFFER_HISTORY_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Offer History Button',
        location: ['Deal Dashboard'],
        intention: 'View Offer History'
    }),
    [interactionAnalyticsActionTypes.CLICK_DEAL_PROGRESS_UPDATE_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Progress Update Button',
        location: ['Deal Dashboard', 'Deal Details', 'Fly Out Modal'],
        intention: 'Update Deal Progress'
    }),
    [interactionAnalyticsActionTypes.CLICK_TRADE_INCLUDE_WITH_OFFER_CHECKBOX]: buildEventWrapper({
        action: 'Click',
        element: 'Trade Include with Offer Checkbox',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Include Trade Vehicle in Offer'
    }),
    [interactionAnalyticsActionTypes.CLICK_START_APPRAISAL_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Start Appraisal Link',
        location: ['Deal Dashboard', 'Trade In'],
        intention: 'Start trade-in appraisal',
        tagIndex: 'dc-mv-210'
    }),
    // Resuming an existing appraisal only — a fresh start fires dc-mv-210
    [interactionAnalyticsActionTypes.CLICK_APPRAISE_TRADE_IN_VEHICLE_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Resume Appraisal Link',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Resume trade-in appraisal',
        tagIndex: 'dc-mv-307'
    }),
    // vAuto's slide panel posts the same message for both Save and Finalize.
    [interactionAnalyticsActionTypes.APPRAISAL_SLIDE_PANEL_CALLBACK_RECEIVED]: buildEventWrapper({
        action: 'Click',
        element: 'Save Drop Down',
        location: ['Deal Dashboard', 'Appraisal Slide Panel'],
        intention: 'Save vAuto trade-in appraisal',
        tagIndex: 'dc-mv-309'
    }),
    [interactionAnalyticsActionTypes.TRADE_IN_INFO_UPDATE_APPLIED]: buildEventWrapper({
        action: 'Click',
        element: 'Trade-In Information Update',
        location: ['Deal Dashboard'],
        intention: 'Update trade-in information',
        tagIndex: 'dc-mv-316'
    }),
    [interactionAnalyticsActionTypes.CLICK_OPEN_REPAIR_ORDERS_LINK]: buildDynamicEvent((action: AnyFSA) => ({
        action: 'Click',
        element: 'Open Repair Orders Link',
        location: ['Deal Dashboard'],
        intention: 'Access a list of open repair orders',
        tagIndex: 'dc-mv-327',
        vehicle: toCdlVehicle(action.payload),
        application: { name: 'Manager View' }
    })),
    [interactionAnalyticsActionTypes.OPEN_REPAIR_ORDERS_SLIDE_OUT_VIEWED]: buildDynamicEvent((action: AnyFSA) => ({
        action: 'View',
        element: 'Open Repair Orders Slide-Out',
        location: ['Deal Dashboard'],
        intention: 'View list of open repair orders',
        tagIndex: 'dc-mv-328',
        vehicle: toCdlVehicle(action.payload),
        application: { name: 'Manager View' }
    })),
    [interactionAnalyticsActionTypes.CLICK_SWAP_BUYER_CO_BUYER_BUTTON]: buildDynamicEvent((action: AnyFSA) => ({
        action: 'Click',
        element: 'Swap Buyer Co-Buyer Button',
        location: ['Deal Dashboard'],
        intention: 'Swap buyer and co-buyer',
        tagIndex: 'dc-mv-329',
        vehicle: toCdlVehicle(action.payload),
        application: { name: 'Manager View' }
    })),
    [interactionAnalyticsActionTypes.CLICK_ADD_ACCESSORIES_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Add Accessories Link',
        location: ['Deal Dashboard', 'Overview', 'Accessories'],
        intention: 'Add Accessories Manually'
    }),
    [interactionAnalyticsActionTypes.CLICK_PROGRAMS_AND_INCENTIVES_ADD_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Programs and Incentives Add Link',
        location: ['Deal Dashboard', 'Incentives'],
        intention: 'View Incentive Details'
    }),
    [interactionAnalyticsActionTypes.CLICK_VEHICLE_ACCESSORIES_START_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Vehicle Accessories Start Link',
        location: ['Deal Dashboard', 'Deal Progress'],
        intention: 'Open Deal Accessories'
    }),
    [interactionAnalyticsActionTypes.CLICK_VEHICLE_ACCESSORIES_EDIT_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Vehicle Accessories Edit Link',
        location: ['Deal Dashboard', 'Deal Progress'],
        intention: 'Edit Deal Accessories'
    }),
    [interactionAnalyticsActionTypes.CLICK_VEHICLE_PROTECTION_START_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Vehicle Protection Start Link',
        location: ['Deal Dashboard', 'Deal Progress'],
        intention: 'Open Deal Protection'
    }),
    [interactionAnalyticsActionTypes.CLICK_VEHICLE_PROTECTION_EDIT_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Vehicle Protection Edit Link',
        location: ['Deal Dashboard', 'Deal Progress'],
        intention: 'Edit Deal Protection'
    }),
    [interactionAnalyticsActionTypes.CLICK_APPLY_VEHICLE_PROTECTION_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Apply Vehicle Protection Button',
        location: ['Deal Dashboard', 'Vehicle Protection'],
        intention: 'Apply Vehicle Protection Plans to Deal'
    }),
    [interactionAnalyticsActionTypes.CLICK_OPEN_FLEXIBLE_PROTECTION_PRODUCTS_ENTRY_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Click Manually Add Protection Product Button',
        location: ['Deal Dashboard Lite', 'Vehicle Protection'],
        intention: 'Open flexible protection products entry fields',
        tagIndex: 'dc-mv-198'
    }),
    [interactionAnalyticsActionTypes.CLICK_REMOVE_MANUALLY_ADDED_PROTECTION_PRODUCT_ICON]: buildEventWrapper({
        action: 'Click',
        element: 'Remove Manually Added Protection Product Icon',
        location: ['Deal Dashboard Lite', 'Vehicle Protection'],
        intention: 'Remove manually added protection product',
        tagIndex: 'dc-mv-199'
    }),
    [interactionAnalyticsActionTypes.CLICK_OPEN_VEHICLE_PROTECTION_FROM_CATALOG_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Open Vehicle Protection From Catalog Link',
        location: ['Deal Dashboard Lite', 'Vehicle Protection'],
        intention: 'Add vehicle protection from catalog',
        tagIndex: 'dc-mv-200'
    }),
    [interactionAnalyticsActionTypes.UPDATE_ADD_PROTECTION_PRODUCT_MANUALLY_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Add Protection Product Manually Text Input',
        location: ['Deal Dashboard Lite', 'Vehicle Protection'],
        intention: 'Add protection product manually',
        tagIndex: 'dc-mv-201'
    }),
    [interactionAnalyticsActionTypes.CLICK_PROGRAMS_AND_INCENTIVES_START_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Incentives Start Link',
        location: ['Deal Dashboard', 'Deal Progress'],
        intention: 'View Deal Incentives'
    }),
    [interactionAnalyticsActionTypes.CLICK_PROGRAMS_AND_INCENTIVES_EDIT_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Incentives Edit Link',
        location: ['Deal Dashboard', 'Deal Progress'],
        intention: 'Edit Deal Incentives'
    }),
    [interactionAnalyticsActionTypes.CLICK_APPLY_PROGRAMS_AND_INCENTIVES_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Apply Incentives Button',
        location: ['Deal Dashboard', 'Incentives'],
        intention: 'Apply Incentives to Deal'
    }),
    [interactionAnalyticsActionTypes.UPDATE_ACQ_FEE_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Lease Offer Acq Fee Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Lease Acquisition Fee'
    }),
    [interactionAnalyticsActionTypes.CLICK_ACQ_FEE_UPFRONT_CHECKBOX]: buildEventWrapper({
        action: 'Click',
        element: 'Lease Offer Acq Fee Upfront Checkbox',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Lease Acquisition Fee Upfront Checkbox'
    }),
    [interactionAnalyticsActionTypes.UPDATE_SECURITY_DEPOSIT_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Lease Offer Security Deposit Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Security Deposit'
    }),
    [interactionAnalyticsActionTypes.UPDATE_COST_PER_MILE_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Lease Offer Cost per Mile Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Lease Cost per Mile'
    }),
    [interactionAnalyticsActionTypes.UPDATE_BASE_RESIDUAL_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Lease Offer Base Residual Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Base Residual'
    }),
    [interactionAnalyticsActionTypes.CLICK_BASE_RESIDUAL_DOLLARS_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Lease Offer Base Residual Dollars Button',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Change Base Residual Type to Dollar'
    }),
    [interactionAnalyticsActionTypes.CLICK_BASE_RESIDUAL_PERCENT_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Lease Offer Base Residual Percent Button',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Change Base Residual Type to Percentage'
    }),
    [interactionAnalyticsActionTypes.SELECT_MILES_PER_YEAR_DROPDOWN]: buildEventWrapper({
        action: 'Select',
        element: 'Lease Offer Miles per Year Dropdown',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Select Desired Lease Miles per Year'
    }),
    [interactionAnalyticsActionTypes.UPDATE_ADJUSTED_RESIDUAL_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Lease Offer Adjusted Residual Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Adjusted Residual'
    }),
    [interactionAnalyticsActionTypes.CLICK_ADJUSTED_RESIDUAL_DOLLARS_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Lease Offer Adjusted Residual Dollars Button',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Change Adjusted Residual Type to Dollar'
    }),
    [interactionAnalyticsActionTypes.CLICK_ADJUSTED_RESIDUAL_PERCENT_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Lease Offer Adjusted Residual Percent Button',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Change Adjusted Residual Type to Percentage'
    }),
    [interactionAnalyticsActionTypes.CLICK_DEAL_TYPE_DROPDOWN_FINANCE]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Type Dropdown',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Deal Type to Finance'
    }),
    [interactionAnalyticsActionTypes.CLICK_DEAL_TYPE_DROPDOWN_LEASE]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Type Dropdown',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Deal Type to Lease'
    }),
    [interactionAnalyticsActionTypes.CLICK_DEAL_TYPE_DROPDOWN_CASH]: buildEventWrapper({
        action: 'Click',
        element: 'Deal Type Dropdown',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Deal Type to Cash'
    }),
    [interactionAnalyticsActionTypes.CLICK_DEAL_LIST_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Deal List Link',
        location: ['Deal Dashboard', 'Header'],
        intention: 'Return to Deal List'
    }),
    [interactionAnalyticsActionTypes.CLICK_ROLL_TO_PAYMENT_APPLY_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Apply Roll-to-Payment',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Apply Roll-to-Payment Changes'
    }),
    [interactionAnalyticsActionTypes.UPDATE_CASH_MSRP_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Cash Offer MSRP Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update MSRP/Retail Price'
    }),
    [interactionAnalyticsActionTypes.UPDATE_CASH_SELLING_PRICE_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Cash Offer Selling Price Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Selling Price'
    }),
    [interactionAnalyticsActionTypes.UPDATE_FINANCE_MSRP_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Finance Offer MSRP Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update MSRP Retail Price'
    }),
    [interactionAnalyticsActionTypes.UPDATE_FINANCE_SELLING_PRICE_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Finance Offer Selling Price Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Selling Price'
    }),
    [interactionAnalyticsActionTypes.UPDATE_FINANCE_CASH_DOWN_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Finance Offer Cash Down Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Cash Down Value'
    }),
    [interactionAnalyticsActionTypes.SELECT_FINANCE_LENDER_DROPDOWN]: buildDynamicEvent((action: AnyFSA, currentState?: any) => {
        const slideOut = currentState ? getCurrentSlideOut(currentState) : 'none';
        const location = ['Deal Dashboard', 'Deal Details'];
        if (slideOut === 'Lender') location.push('Lender Slideout');
        const { lenderId, lenderName } = action.payload ?? {};
        return {
            action: 'Select',
            element: 'Finance Offer Lender Dropdown',
            location,
            intention: 'Select Lender',
            tagIndex: 'dc-mv-55',
            vehicle: toCdlVehicle(action.payload?.vehicleInfo),
            deal: {
                lenderId: lenderId && lenderName ? `${lenderId}_${lenderName}` : lenderId
            }
        };
    }),
    [interactionAnalyticsActionTypes.UPDATE_FINANCE_TERM_DROPDOWN]: buildEventWrapper({
        action: 'Update',
        element: 'Finance Offer Term Update',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Finance Term Duration'
    }),
    [interactionAnalyticsActionTypes.UPDATE_LEASE_MSRP_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Lease Offer MSRP Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update MSRP/Retail Price'
    }),
    [interactionAnalyticsActionTypes.UPDATE_LEASE_SELLING_PRICE_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Lease Offer Selling Price Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Selling Price'
    }),
    [interactionAnalyticsActionTypes.UPDATE_LEASE_CASH_DOWN_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Lease Offer Cash Down Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Cash Down Value'
    }),
    [interactionAnalyticsActionTypes.SELECT_LEASE_LENDER_DROPDOWN]: buildDynamicEvent((action: AnyFSA, currentState?: any) => {
        const slideOut = currentState ? getCurrentSlideOut(currentState) : 'none';
        const location = ['Deal Dashboard', 'Deal Details'];
        if (slideOut === 'Lender') location.push('Lender Slideout');
        const { lenderId, lenderName } = action.payload ?? {};
        return {
            action: 'Select',
            element: 'Lease Offer Lender Dropdown',
            location,
            intention: 'Select Lender',
            tagIndex: 'dc-mv-98',
            vehicle: toCdlVehicle(action.payload?.vehicleInfo),
            deal: {
                lenderId: lenderId && lenderName ? `${lenderId}_${lenderName}` : lenderId
            }
        };
    }),
    [interactionAnalyticsActionTypes.UPDATE_LEASE_TERM_DROPDOWN]: buildEventWrapper({
        action: 'Update',
        element: 'Lease Offer Term Update',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Lease Term Duration'
    }),
    [interactionAnalyticsActionTypes.CLICK_GENERATE_CONTRACT_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Generate Contract Button',
        location: ['Deal Dashboard', 'Footer'],
        intention: 'Generate Contract'
    }),
    [interactionAnalyticsActionTypes.CLICK_GENERATE_CONTRACT_CONFIRM_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Generate Contract Confirm Button',
        location: ['Deal Dashboard', 'Footer', 'Modal'],
        intention: 'Confirm Generate Contract'
    }),
    [interactionAnalyticsActionTypes.CLICK_MANAGE_DOCUMENTS_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Manage Documents',
        location: ['Deal Dashboard', 'Footer'],
        intention: 'Open Digital Deal Jacket'
    }),
    [interactionAnalyticsActionTypes.CLICK_PRINT_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Print Button',
        location: ['Deal Dashboard', 'Footer'],
        intention: 'Print Deal Document'
    }),
    [interactionAnalyticsActionTypes.CLICK_FUNCTIONS_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Functions Button',
        location: ['Deal Dashboard', 'Footer'],
        intention: 'Show Hotkeys'
    }),
    [interactionAnalyticsActionTypes.SELECT_FINANCE_LENDER_DECISION_DROPDOWN]: buildEventWrapper({
        action: 'Select',
        element: 'Finance Offer Lender Decision Dropdown',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Select Lender Decision'
    }),
    [interactionAnalyticsActionTypes.UPDATE_FINANCE_SELL_RATE_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Finance Offer Sell Rate Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Sell APR'
    }),
    [interactionAnalyticsActionTypes.UPDATE_FINANCE_BUY_RATE_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Finance Offer Buy Rate Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Buy APR'
    }),
    [interactionAnalyticsActionTypes.SELECT_LEASE_LENDER_DECISION_DROPDOWN]: buildEventWrapper({
        action: 'Select',
        element: 'Lease Offer Lender Decision Dropdown',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Select Lender Decision'
    }),
    [interactionAnalyticsActionTypes.UPDATE_LEASE_SELL_RATE_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Lease Offer Sell Rate Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Sell Rate'
    }),
    [interactionAnalyticsActionTypes.UPDATE_LEASE_BUY_RATE_TEXT_INPUT]: buildEventWrapper({
        action: 'Update',
        element: 'Lease Offer Buy Rate Text Input',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Update Buy Rate'
    }),
    [interactionAnalyticsActionTypes.CLICK_PUSH_TO_DARWIN_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Push to Darwin Button',
        location: ['Deal Dashboard', 'Footer'],
        intention: 'Navigate to Darwin',
        tagIndex: 'dc-mv-182'
    }),
    [interactionAnalyticsActionTypes.OPEN_IN_DARWIN_BUTTON]: buildEventWrapper({
        action: 'Click',
        element: 'Open in Darwin Button',
        location: ['Deal Dashboard', 'Deal Details'],
        intention: 'Navigate to Darwin'
    }),
    [interactionAnalyticsActionTypes.ADD_ANOTHER_TRADEIN_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Add Another Trade-In Link',
        location: ['Deal Dashboard'],
        intention: 'Add another trade-in'
    }),
    [interactionAnalyticsActionTypes.DELETE_TRADEIN_ICON]: buildEventWrapper({
        action: 'Click',
        element: 'Delete Trade-In Icon',
        location: ['Deal Dashboard'],
        intention: 'Delete Trade-In'
    }),
    [interactionAnalyticsActionTypes.TRADEIN_VEHICLE_ACCORDION_SUMMARY]: buildEventWrapper({
        action: 'Click',
        element: 'Trade-In Vehicle Accordion Summary',
        location: ['Deal Dashboard'],
        intention: 'Select trade-in vehicle'
    }),
    [interactionAnalyticsActionTypes.CLICK_ALTERNATIVE_VEHICLE_OPTIONS_LINK]: buildDynamicEvent((action: AnyFSA) => ({
        action: 'Click',
        element: 'Alternative Vehicle Options Link',
        location: ['Deal Dashboard'],
        intention: 'View AI supported vehicle suggestions',
        tagIndex: 'dc-mv-257',
        vehicle: toCdlVehicle(action.payload),
        application: {
            name: 'Manager View'
        }
    })),
    [interactionAnalyticsActionTypes.CLICK_LENDER_APPROVAL_LIKELIHOOD_LINK]: buildDynamicEvent((action: AnyFSA) => {
        const { lenderId, lenderName } = action.payload ?? {};
        return {
            action: 'Click',
            element: 'Lender Approval Likelihood Link',
            location: ['Deal Dashboard'],
            intention: 'View AI supported lender suggestion',
            tagIndex: 'dc-mv-258',
            vehicle: toCdlVehicle(action.payload?.vehicle),
            deal: {
                lenderId: lenderId && lenderName ? `${lenderId}_${lenderName}` : lenderId,
                loanLikelihood: action.payload?.loanLikelihood,
                buyRate: action.payload?.buyRate
            },
            application: {
                name: 'Manager View'
            }
        };
    }),
    [interactionAnalyticsActionTypes.LENDER_APPROVAL_LIKELIHOOD_VIEWED]: buildDynamicEvent((action: AnyFSA) => {
        const { lenderId, lenderName } = action.payload ?? {};
        return {
            action: 'View',
            element: 'Lender Approval Likelihood Summary View',
            location: ['Deal Dashboard'],
            intention: 'View and compare lender approval likelihood scores',
            tagIndex: 'dc-mv-259',
            vehicle: toCdlVehicle(action.payload?.vehicle),
            deal: {
                lenderId: lenderId && lenderName ? `${lenderId}_${lenderName}` : lenderId,
                loanLikelihood: action.payload?.loanLikelihood,
                buyRate: action.payload?.buyRate
            },
            application: {
                name: 'Manager View'
            }
        };
    }),
    [pushToRouteOneActionTypes.CONFIRM_PUSH_TO_ROUTEONE]: buildEventWrapper({
        action: 'Click',
        element: 'Push to RouteOne Confirm Button',
        location: ['Deal Dashboard'],
        intention: 'Navigate to RouteOne',
        tagIndex: 'dc-mv-264'
    }),
    [interactionAnalyticsActionTypes.CLICK_MV_ACCESSORIES_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Accessories Link',
        location: ['Deal Dashboard'],
        intention: 'Navigate to accessories information',
        tagIndex: 'dc-mv-265'
    }),
    [interactionAnalyticsActionTypes.CLICK_MV_LENDER_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Lender Link',
        location: ['Deal Dashboard'],
        intention: 'Navigate to lender information',
        tagIndex: 'dc-mv-266'
    }),
    [interactionAnalyticsActionTypes.CLICK_MV_INCENTIVES_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Incentives Link',
        location: ['Deal Dashboard'],
        intention: 'Navigate to incentives information',
        tagIndex: 'dc-mv-267'
    }),
    [interactionAnalyticsActionTypes.CLICK_MV_PROTECTION_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Protection Products Link',
        location: ['Deal Dashboard'],
        intention: 'Navigate to vehicle protection information',
        tagIndex: 'dc-mv-268'
    }),
    [interactionAnalyticsActionTypes.CLICK_MV_TAXES_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Tax Link',
        location: ['Deal Dashboard'],
        intention: 'Navigate to tax information',
        tagIndex: 'dc-mv-269'
    }),
    [interactionAnalyticsActionTypes.CLICK_MV_FEES_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Fees Link',
        location: ['Deal Dashboard'],
        intention: 'Navigate to fees information',
        tagIndex: 'dc-mv-270'
    }),
    [interactionAnalyticsActionTypes.CLICK_MV_TRADE_ALLOWANCE_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Trade Allowance Link',
        location: ['Deal Dashboard'],
        intention: 'Navigate to trade allowance information',
        tagIndex: 'dc-mv-271'
    }),
    [interactionAnalyticsActionTypes.CLICK_MV_TRADE_PAYOFF_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Trade Payoff Link',
        location: ['Deal Dashboard'],
        intention: 'Navigate to trade payoff information',
        tagIndex: 'dc-mv-272'
    }),
    [dueAtSigningActionTypes.DUE_AT_SIGNING_SLIDE_OUT_TOGGLE]: buildEventWrapper({
        action: 'Click',
        element: 'Due at Signing Link',
        location: ['Deal Dashboard'],
        intention: 'Navigate to due at signing information',
        tagIndex: 'dc-mv-273'
    }),
    [interactionAnalyticsActionTypes.CLICK_MV_CHANGE_VEHICLE]: buildEventWrapper({
        action: 'Click',
        element: 'Change Vehicle Link',
        location: ['Deal Dashboard'],
        intention: 'Change vehicle',
        tagIndex: 'dc-mv-274'
    }),
    [interactionAnalyticsActionTypes.CLICK_MV_CHANGE_TO_FI_MODE]: buildEventWrapper({
        action: 'Click',
        element: 'Change to F&I Mode',
        location: ['Deal Dashboard'],
        intention: 'Switch to F&I Mode',
        tagIndex: 'dc-mv-275'
    }),
    [interactionAnalyticsActionTypes.CLICK_MV_CHANGE_TO_DESKING_MODE]: buildEventWrapper({
        action: 'Click',
        element: 'Change to Desking Mode',
        location: ['Deal Dashboard'],
        intention: 'Switch to Desking Mode',
        tagIndex: 'dc-mv-276'
    }),
    [interactionAnalyticsActionTypes.CLICK_MV_RATE_LINK]: buildEventWrapper({
        action: 'Click',
        element: 'Rate Link',
        location: ['Deal Dashboard'],
        intention: 'Navigate to lender rate information',
        tagIndex: 'dc-mv-284'
    }),

    // AI Analytics Events (CDL3 Desking Intelligence)
    [deskingActionTypes.AI_REFRESH_CLICKED]: buildDynamicEvent((action: AnyFSA) => {
        const topLender = action.payload?.lenderProbabilities?.[0];
        const { lenderId, lenderName } = topLender ?? {};
        return {
            action: 'Click',
            element: 'AI Refresh Button',
            location: ['Deal Dashboard'],
            intention: 'Refresh AI indicators with updated terms',
            tagIndex: 'dc-mv-260',
            vehicle: toCdlVehicle(action.payload?.vehicleInfo),
            deal: {
                lenderId: lenderId && lenderName ? `${lenderId}_${lenderName}` : lenderId,
                loanLikelihood: topLender?.loanLikelihood
            },
            application: {
                name: 'Manager View'
            }
        };
    }),
    [deskingActionTypes.VEHICLE_RECOMMENDATION_DISPLAYED]: buildDynamicEvent((action: AnyFSA) => ({
        action: 'View',
        element: 'AI Supported Vehicle Suggestion Card',
        location: ['Deal Dashboard'],
        intention: 'Show AI based alternative vehicle selection',
        tagIndex: 'dc-mv-261',
        vehicle: {
            ...toCdlVehicle(action.payload?.vehicleInfo),
            cadsVehicleCatalogId: action.payload?.vehicleInfo?.cadsVehicleCatalogId,
            acceptanceLikelihood: action.payload?.vehicleInfo?.acceptanceLikelihood
        },
        application: {
            name: 'Manager View'
        }
    })),
    [deskingActionTypes.VEHICLE_RECOMMENDATION_SELECTED]: buildDynamicEvent((action: AnyFSA) => {
        const { lenderId, lenderName } = action.payload?.vehicleInfo ?? {};
        return {
            action: 'Click',
            element: 'Alternative Vehicle Options Card',
            location: ['Deal Dashboard'],
            intention: 'Select vehicle',
            tagIndex: 'dc-mv-285',
            vehicle: {
                ...toCdlVehicle(action.payload?.vehicleInfo),
                cadsVehicleCatalogId: action.payload?.vehicleInfo?.cadsVehicleCatalogId,
                acceptanceLikelihood: action.payload?.vehicleInfo?.loanLikelihood
            },
            deal: {
                lenderId: lenderId && lenderName ? `${lenderId}_${lenderName}` : lenderId
            },
            application: {
                name: 'Manager View'
            }
        };
    }),
    [deskingActionTypes.SOFTPULL_CONSENT_CONTINUE_CLICKED]: buildDynamicEvent((action: AnyFSA) => ({
        action: 'Click',
        element: 'Credit Pull Consent Confirmation Button',
        location: ['Deal Dashboard'],
        intention: 'Confirm soft credit pull',
        tagIndex: 'dc-mv-277',
        vehicle: toCdlVehicle(action.payload?.vehicleInfo),
        application: {
            name: 'Manager View'
        }
    })),
    [deskingActionTypes.SOFTPULL_CONSENT_CANCEL_CLICKED]: buildDynamicEvent((action: AnyFSA) => ({
        action: 'Click',
        element: 'Credit Pull Consent Cancellation Link',
        location: ['Deal Dashboard'],
        intention: 'Cancel soft credit pull',
        tagIndex: 'dc-mv-283',
        vehicle: toCdlVehicle(action.payload?.vehicleInfo)
    })),
    [deskingActionTypes.LENDER_PROBABILITY_DISPLAYED]: buildDynamicEvent((action: AnyFSA) => {
        const { lenderId, lenderName } = action.payload?.lenderInfo ?? {};
        return {
            action: 'View',
            element: 'Lender Approval Likelihood Comparison',
            location: ['Deal Dashboard'],
            intention: 'View lender likelihood comparison',
            tagIndex: 'dc-mv-278',
            vehicle: toCdlVehicle(action.payload?.vehicleInfo),
            deal: {
                lenderId: lenderId && lenderName ? `${lenderId}_${lenderName}` : lenderId,
                loanLikelihood: action.payload?.lenderInfo?.loanLikelihood,
                buyRate: action.payload?.lenderInfo?.buyRate,
                // ILS-powered backend limit — free-form text, forwarded as-is (`null` when the lender
                // publishes no limit).
                backendLimit: action.payload?.lenderInfo?.backendLimit
            },
            application: {
                name: 'Manager View'
            }
        };
    }),
    [deskingActionTypes.LENDER_RETURN_TO_OFFER_CLICKED]: buildDynamicEvent((action: AnyFSA) => {
        const topLender = action.payload?.lenderProbabilities?.[0];
        const { lenderId, lenderName } = topLender ?? {};
        return {
            action: 'Click',
            element: 'Return to Offer Button',
            location: ['Deal Dashboard'],
            intention: 'Return to offer',
            tagIndex: 'dc-mv-279',
            vehicle: toCdlVehicle(action.payload?.vehicleInfo),
            deal: {
                lenderId: lenderId && lenderName ? `${lenderId}_${lenderName}` : lenderId,
                loanLikelihood: topLender?.loanLikelihood,
                buyRate: topLender?.buyRate,
                // ILS-powered backend limit — see dc-mv-278 above.
                backendLimit: topLender?.backendLimit
            },
            application: {
                name: 'Manager View'
            }
        };
    }),
    [deskingActionTypes.LENDER_BACKEND_LIMIT_DETAILS_CLICKED]: buildDynamicEvent((action: AnyFSA) => {
        const { lenderId, lenderName } = action.payload?.lenderInfo ?? {};
        return {
            action: 'Click',
            element: 'View Backend Limit Details',
            location: ['Deal Dashboard'],
            intention: 'Open backend limit detail information by lender',
            tagIndex: 'dc-mv-321',
            vehicle: toCdlVehicle(action.payload?.vehicleInfo),
            deal: {
                lenderId: lenderId && lenderName ? `${lenderId}_${lenderName}` : lenderId,
                loanLikelihood: action.payload?.lenderInfo?.loanLikelihood,
                buyRate: action.payload?.lenderInfo?.buyRate,
                // The limit whose details were opened — free-form multi-line text, forwarded as-is.
                backendLimit: action.payload?.lenderInfo?.backendLimit
            },
            application: {
                name: 'Manager View'
            }
        };
    }),
    [deskingActionTypes.SOFTPULL_ALT_CONSENT_CONTINUE_CLICKED]: buildDynamicEvent((action: AnyFSA) => ({
        action: 'Click',
        element: 'Soft Pull Acknowledgement Confirmation Button',
        location: ['Deal Dashboard'],
        intention: 'Confirm credit soft pull',
        tagIndex: 'dc-mv-281',
        vehicle: toCdlVehicle(action.payload?.vehicleInfo),
        application: {
            name: 'Manager View'
        }
    })),
    [deskingActionTypes.SOFTPULL_MANDATORY_DETAILS_CONTINUE_CLICKED]: buildDynamicEvent((action: AnyFSA) => ({
        action: 'Click',
        element: 'Update Customer and Initiate Soft Pull',
        location: ['Deal Dashboard'],
        intention: 'Initiate soft pull to support full AI capability',
        tagIndex: 'dc-mv-300',
        vehicle: toCdlVehicle(action.payload?.vehicleInfo),
        application: {
            name: 'Manager View'
        }
    })),

    // AI Alternative Deal Structure Analytics (CDL3)
    [deskingActionTypes.ALT_DEAL_STRUCTURE_LINK_CLICKED]: buildDynamicEvent((action: AnyFSA) => ({
        action: 'Click',
        element: 'Alternative Deal Structure Link',
        location: ['Deal Dashboard'],
        intention: 'View AI generated deal type structures',
        tagIndex: 'dc-mv-288',
        vehicle: toCdlVehicle(action.payload?.vehicleInfo),
        application: {
            name: 'Manager View'
        }
    })),
    [deskingActionTypes.ALT_DEAL_STRUCTURE_OPTIONS_VIEWED]: buildDynamicEvent((action: AnyFSA) => ({
        action: 'View',
        element: 'Alternative Deal Structure Options',
        location: ['Deal Dashboard'],
        intention: 'View alternative deal structure options by deal type',
        tagIndex: 'dc-mv-289',
        vehicle: toCdlVehicle(action.payload?.vehicleInfo),
        deal: buildAltDealStructureDeal(action.payload?.dealStructure),
        application: {
            name: 'Manager View'
        }
    })),
    [deskingActionTypes.ALT_DEAL_STRUCTURE_CREATE_SCENARIO_CLICKED]: buildDynamicEvent((action: AnyFSA) => ({
        action: 'Click',
        element: 'Create Scenario Button',
        location: ['Deal Dashboard'],
        intention: 'Create deal scenario with AI supported deal structure information',
        tagIndex: 'dc-mv-290',
        vehicle: toCdlVehicle(action.payload?.vehicleInfo),
        deal: buildAltDealStructureDeal(action.payload?.dealStructure),
        application: {
            name: 'Manager View'
        }
    })),
    [deskingActionTypes.ALT_DEAL_STRUCTURE_CANCEL_CLICKED]: buildDynamicEvent((action: AnyFSA) => ({
        action: 'Click',
        element: 'Cancel Scenario Button',
        location: ['Deal Dashboard'],
        intention: 'Exit AI supported deal structure options without saving',
        tagIndex: 'dc-mv-291',
        vehicle: toCdlVehicle(action.payload?.vehicleInfo),
        deal: buildAltDealStructureDeal(action.payload?.dealStructure),
        application: {
            name: 'Manager View'
        }
    })),
    [deskingActionTypes.ALT_DEAL_STRUCTURE_TERM_DOWN_PAYMENT_UPDATED]: buildDynamicEvent((action: AnyFSA) => ({
        action: 'Click',
        element: 'Term and Downpayment Updates',
        location: ['Deal Dashboard'],
        intention: 'Editing AI proposed deal structure',
        tagIndex: 'dc-mv-292',
        vehicle: toCdlVehicle(action.payload?.vehicleInfo),
        deal: buildAltDealStructureDeal(action.payload?.dealStructure),
        application: {
            name: 'Manager View'
        }
    })),
    [deskingActionTypes.ALT_DEAL_STRUCTURE_RETRY_RATES_CLICKED]: buildDynamicEvent((action: AnyFSA) => ({
        action: 'Click',
        element: 'Retry Rates Link',
        location: ['Deal Dashboard'],
        intention: 'Update deal structure overview',
        tagIndex: 'dc-mv-296',
        vehicle: toCdlVehicle(action.payload?.vehicleInfo),
        deal: buildAltDealStructureDeal(action.payload?.dealStructure),
        application: {
            name: 'Manager View'
        }
    })),

    // DealPulse Analytics (CDL3)
    [baseDealActionTypes.DEAL_PULSE_SLIDEOUT_VIEWED]: buildDynamicEvent((action: AnyFSA) => {
        const insights = action.payload?.dealPulseInsights ?? {};
        const tradeInRecord = action.payload?.tradeInRecord;
        return {
            action: 'View',
            element: 'DealPulse Slideout',
            location: ['Deal Dashboard'],
            intention: 'Provide detailled DealPulse information',
            tagIndex: 'dc-mv-306',
            vehicle: {
                ...toCdlVehicle(action.payload?.vehicleInfo),
                predictions: {
                    make: insights.predictedTradeInMake ?? 'n/a',
                    model: insights.predictedTradeInModel ?? 'n/a',
                    year: insights.predictedTradeInYear ?? 'n/a'
                }
            },
            deal: {
                predictions: {
                    offerPrice: 'n/a',
                    offerPriceMin: toDealPulseValue(insights.preferredPriceLow),
                    offerPriceMax: toDealPulseValue(insights.preferredPriceHigh),
                    downPayment: 'n/a',
                    downPaymentMin: toDealPulseValue(insights.preferredDownLow),
                    downPaymentMax: toDealPulseValue(insights.preferredDownHigh),
                    monthlyPayment: 'n/a',
                    monthlyPaymentMin: toDealPulseValue(insights.preferredMonthlyLow),
                    monthlyPaymentMax: toDealPulseValue(insights.preferredMonthlyHigh),
                    tradeAllowance: toDealPulseValue(tradeInRecord?.tradeValueMid),
                    tradeAllowanceMin: toDealPulseValue(tradeInRecord?.tradeValueLow),
                    tradeAllowanceMax: toDealPulseValue(tradeInRecord?.tradeValueHigh),
                    tradeVin: tradeInRecord?.vin || 'n/a',
                    tradeLikelihood: 'n/a'
                }
            },
            application: {
                name: 'Manager View'
            }
        };
    }),

    [fuelSavingActionTypes.FUEL_SAVING_OPEN]: buildEventWrapper({
        action: 'Click',
        element: 'Open Fuel Savings Analysis Button',
        location: ['Deal Dashboard'],
        intention: 'Opens fuel savings analysis slideout',
        tagIndex: 'dc-mv-303'
    }),
    [fuelEconomyActionTypes.APPLY_FUEL_SAVING_CDL]: buildEventWrapper({
        action: 'Click',
        element: 'Apply Fuel Savings Analysis Button',
        location: ['Deal Dashboard'],
        intention: 'Saves fuel savings analysis data comparison',
        tagIndex: 'dc-mv-304'
    }),
    [fuelEconomyActionTypes.APPLY_AND_PRINT_FUEL_SAVING_CDL]: buildEventWrapper({
        action: 'Click',
        element: 'Apply Fuel Savings Analysis and Print Button',
        location: ['Deal Dashboard'],
        intention: 'Saves fuel savings analysis data comparison and prints proposal for customer',
        tagIndex: 'dc-mv-305'
    }),
    [tradeReconCostActionTypes.SAVE_TRADE_RECON_COST]: buildEventWrapper({
        action: 'Click',
        element: 'Apply Trade-In Reconditioning Amounts Button',
        location: ['Deal Dashboard'],
        intention: 'Apply itemized reconditioning costs to each trade-in',
        tagIndex: 'dc-mv-301'
    }),
    [tradeReconCostActionTypes.PRINT_TRADE_RECON_COST]: buildEventWrapper({
        action: 'Click',
        element: 'Apply Trade-In Reconditioning Amounts and Print Overview Button',
        location: ['Deal Dashboard'],
        intention: 'Apply itemized reconditioning costs to each trade-in and print overview',
        tagIndex: 'dc-mv-302'
    }),
    [offerReduxActions.UPDATED_PAYMENT_GRID_TOGGLE]: paymentGridToggleHandler,
    [offerReduxActions.UPDATED_FLEX_BUY_PAYMENT_GRID_TOGGLE]: paymentGridToggleHandler,
    // dc-mv-311: ASP checkbox toggle. Rides on the existing `UPDATE_CURRENT_CALCULATION_ENDPOINT`
    // dispatch (which is broadly re-used across the app, per F-2 — DealTypeDropdown, payment
    // middleware, programs middleware, etc.) and only fires when the dispatch site explicitly
    // marks itself with `meta.userIntent === 'asp-checkbox-toggled'`. The toggle boolean is
    // carried on `meta.analyticsPayload.toggleState` because the payload's `calculationEndpoint`
    // string ('program-quotes' vs 'user-program-quotes') is a fragile proxy for user intent.
    [offerReduxActions.UPDATE_CURRENT_CALCULATION_ENDPOINT]: buildDynamicEvent(
        (action: AnyFSA) => ({
            action: 'Click',
            element: 'Auto Select Program Checkbox',
            location: ['Deal Dashboard', 'Deal Details', 'Auto Select Programs'],
            intention: 'Enable Auto-Select Program for the Deal',
            tagIndex: 'dc-mv-311',
            checkBoxValue: action.meta.analyticsPayload.toggleState,
            application: { name: 'Manager View' }
        }),
        (action: AnyFSA) =>
            action.meta?.userIntent === CDL_USER_INTENT.ASP_CHECKBOX_TOGGLED &&
            typeof action.meta.analyticsPayload?.toggleState === 'boolean'
    ),
    // dc-mv-313: Payment Grid term and cash text-entry edits both fire under the same tag, with
    // `element`/`intention` disambiguating term vs cash for downstream Pixall segmentation.
    // Naming note: the spec wording is "Cash Amount Paid" (used in `intention`), but the existing
    // action type and codebase column name is "Cash Down" (kept in `element`). Tagging Spec owner
    // confirmation is pending (Bundle 5 R-3) — if the spec re-points to a different field, the
    // `intention` string here may need to be updated.
    [offerReduxActions.UPDATE_PAYMENT_GRID_TERM]: buildDynamicEvent(
        (action: AnyFSA) => ({
            action: 'Update',
            element: 'Payment Grid Term Textbox',
            location: ['Deal Dashboard', 'Deal Details', 'Payment Grid'],
            intention: 'Edit Payment Grid term',
            tagIndex: 'dc-mv-314',
            textBoxValue: action.payload.val,
            application: { name: 'Manager View' }
        }),
        (action: AnyFSA) =>
            action.meta?.userIntent === CDL_USER_INTENT.PAYMENT_GRID_TEXT_ENTRY &&
            typeof action.payload.offerType === 'string' &&
            typeof action.payload.val === 'number'
    ),
    [offerReduxActions.UPDATE_PAYMENT_GRID_CASH_DOWN]: buildDynamicEvent(
        (action: AnyFSA) => ({
            action: 'Update',
            element: 'Payment Grid Down Payment Textbox',
            location: ['Deal Dashboard', 'Deal Details', 'Payment Grid'],
            intention: 'Edit Payment Grid Down Payment',
            tagIndex: 'dc-mv-313',
            textBoxValue: action.payload.val,
            application: { name: 'Manager View' }
        }),
        (action: AnyFSA) =>
            action.meta?.userIntent === CDL_USER_INTENT.PAYMENT_GRID_TEXT_ENTRY &&
            typeof action.payload.offerType === 'string' &&
            typeof action.payload.val === 'number'
    ),
    // dc-mv-315: Payment Grid Sell Rate edit. Rides on the existing `UPDATED_SELL_RATE_OVERRIDE`
    // dispatch and only fires when the dispatch site marks itself with
    // `meta.userIntent === 'payment-grid-sell-rate'`. `paymentGridMiddleware` fans the user
    // action out to every Cash Down column via `addPaymentGridMeta`, which preserves
    // `meta.userIntent` on each fan-out copy — so the userIntent gate alone would fire once per
    // column. Guard on `meta.paymentGrid?.gridAction !== true` to reject fanned-out re-dispatches
    // (stamped by `addPaymentGridMeta`) while still admitting the original user click.
    [offerReduxActions.UPDATED_SELL_RATE_OVERRIDE]: buildDynamicEvent(
        (action: AnyFSA) => ({
            action: 'Update',
            element: 'Payment Grid Sell Rate TextBox',
            location: ['Deal Dashboard', 'Deal Details', 'Payment Grid'],
            intention: 'Edit Payment Grid Rate',
            tagIndex: 'dc-mv-315',
            textBoxValue: action.payload.sellRate,
            application: { name: 'Manager View' }
        }),
        (action: AnyFSA) =>
            action.meta?.userIntent === CDL_USER_INTENT.PAYMENT_GRID_SELL_RATE && action.meta.paymentGrid?.gridAction !== true
    )
};

export const cdlV3AnalyticsEventMiddleware = (middlewareArgs: MiddlewareAPI) => {
    const dataReadyEvent = {
        CDLV3_DATA_READY: () => {
            const eventData = {
                // add more data here if avaiable in later stages and needed for initial CDL3 setup (part of 49 fields)
            };
            isCdlDataReady = true;
            for (const oldEvent of eventsBeforeDataReady) {
                middlewareArgs.dispatch(oldEvent);
            }
            return buildReadyEventV3(eventData as BaseDataLayerV3);
        }
    };
    const eventMap: EventMapV3 = {
        ...dataReadyEvent,
        ...userEvents,
        ...openDealCdlEvents
    };
    return buildCDLEventMiddlewareV3({
        eventMap,
        enabled: true,
        middlewareEnabledSelector: () => true,
        alwaysIncludeAppName: true // addresses defect DE751628
    })(middlewareArgs);
};
